import { CITIES, PROVINCES } from 'containers/MapSearch/constants'
import useFindingLocations from './hooks/useFindingLocations'
import { selectPlaces } from 'containers/Maps/Map/mapSlice'
import React, { useEffect, useRef, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import useDevice from 'hooks/useDevice'
import { useReduxSelector } from 'store'
import { v7 } from 'uuid'

interface MapOption {
  mapPosition?: [number, number]
  position?: [number, number]
  showMarker: boolean
  voivodship?: number
  zoom?: number
}

interface LocationSelectProps {
  voivodship?: number
  disabled?: boolean
  address: string
  label?: boolean
  showIcon?: boolean
  placeholder?: string
  secondaryTheme?: boolean
  setAddress: (address: string, mapOptions: MapOption) => void
  clearAddress?: basic.BasicFunc
}

interface Place {
  id: string
  mm_score: number
  placeName: string
  additionalDescription: string
  shortcutName: string
  marker_color: string
  marker_text: string
  closest_availability_date: string
  street: string
  estateNumber: string
  apartmentNumber: any
  zip: string
  city: string
  district: any
  province: string
  country: string
  gpslatitude: string
  gpslongitude: string
  restartCarousel: boolean
  total_free: number
  free_with_online_access: number
  free_with_lock_access: number
  constructionDate: any
  distance: any
  min_month_price: number
  min_day_price: string
  min_renting_days: number
  min_sqm_price: string
}

interface Province {
  name: string
  search: string
  capitol: string
  value: number
  amount?: number
  gpslatitude: number
  gpslongitude: number
}

interface City {
  city: string
  district: string
  gpslatitude: number
  gpslongitude: number
  amount?: number
  radius: number
}

const id = v7()

const LocationSelect: React.FC<LocationSelectProps> = ({ label = true, disabled = false, voivodship = -1, address = '', secondaryTheme, showIcon, placeholder, setAddress, clearAddress }) => {
  const device = useDevice()
  const mobile = device.isMobile
  const [expanded, expand] = useState(false)
  const [reversed, setReverse] = useState(false)
  const mapLocations = useReduxSelector(selectPlaces)
  const [provinceIdx, setProvinceIdx] = useState(voivodship)
  const { input, isLoading, addressList, setInput } = useFindingLocations(address)

  const containerRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)
  const preventClick = useRef(false)

  const { t } = useTranslation('filters')
  const checkIsReversed = () => {
    const container = containerRef.current
    if (!container) return
    const dimensions = container.getBoundingClientRect()
    setReverse(dimensions.y > (window.innerHeight / 2))
  }

  useEffect(() => {
    checkIsReversed()
  }, [])

  useEffect(() => {
    optionsRef.current?.scrollTo({ top: 0 })
  }, [provinceIdx])

  useEffect(() => {
    const container = containerRef.current
    if (container && expanded) {
      const unfocus = (event: Event) => {
        if (!container.contains(event.target as Node)) {
          if (preventClick.current) {
            preventClick.current = false
          } else {
            expand(false)
          }
        }
      }
      const root = document.querySelector('#root')
      root?.addEventListener('click', unfocus)
      checkIsReversed()
      return () => {
        root?.removeEventListener('click', unfocus)
      }
    }
  }, [expanded])

  return (
    <div
      ref={containerRef}
      className={`select__container${expanded ? ' --expanded' : ''}${mobile ? ' --mobile' : ''}${reversed ? ' --reverse' : ''}${secondaryTheme ? ' --secTheme' : ''}`}>
      <div className='input__default'>
        {label && <label className='input__label' htmlFor={id}>
          <span>{t('location')}</span>
        </label>}

        <div className='input__main'>
          <div
            className='select'
            onClick={() => {
              if (disabled) return
              if (!expanded) expand(true)
            }}>
            {showIcon && <i className='icon icon--location-500' />}
            <span>
              <input
                id={id}
                value={input}
                className='input'
                placeholder={placeholder || t('enterAddress')}
                onChange={e => {
                  setInput(e.target.value)
                  if (provinceIdx > -1) setProvinceIdx(-1)
                }}
              />
            </span>
            {input && <i
              className='icon icon--close'
              onClick={e => {
                e.stopPropagation()
                preventClick.current = true
                clearAddress?.()
                setInput('')
              }} />}
            <i
              className={`icon icon--chevron-${reversed ? expanded ? 'down' : 'up' : expanded ? 'up' : 'down'}`}
              onClick={() => {
                if (expanded) expand(false)
              }} />
          </div>
        </div>
      </div>

      <div
        ref={optionsRef}
        className='select__options'>
        {input
          ? (() => {
            const validProvinces = PROVINCES.filter(p => p.search.includes(input.toLowerCase()))
            return <>
              {isLoading && <div className='select__option center'>
                <i className='icon icon--loading' />
              </div>}
              {validProvinces.map((option) => <div
                key={option.search}
                className='select__option group'
                onClick={() => {
                  expand(false)
                  setInput(option.name)
                  setAddress(option.name, {
                    mapPosition: [option.gpslatitude, option.gpslongitude],
                    voivodship: option.value,
                    showMarker: false
                  })
                }}>
                <span>woj. {option.name} ({getPlacesAmountInProvince(option.value, mapLocations)})</span>
                <i className='icon icon--arrow-right' />
              </div>)}
              {addressList?.map((option) => {
                const name = `${option.city}, ${option.province}`
                return <div
                  key={name}
                  className='select__option group'
                  onClick={() => {
                    expand(false)
                    setInput(name)
                    setAddress(name, {
                      position: [option.gpslatitude, option.gpslongitude],
                      showMarker: true
                    })
                  }}>
                  <span>{option.city}, pow. {option.district}</span>
                  <i className='icon icon--arrow-right' />
                </div>
              })}
              {!isLoading && validProvinces.length === 0 && (!addressList || addressList.length === 0) && <div className='select__option'>
                <span>{t('notFound')}</span>
              </div>}
            </>
          })()
          : provinceIdx > -1
            ? <>
              <div
                className='select__option'
                onClick={() => {
                  preventClick.current = true
                  setProvinceIdx(-1)
                }}>
                <span>{t('back')}</span>
              </div>
              <div
                className='select__option group'
                onClick={() => {
                  expand(false)
                  setProvinceIdx(-1)
                  setInput(PROVINCES[provinceIdx].name)
                  setAddress(PROVINCES[provinceIdx].name, {
                    mapPosition: [PROVINCES[provinceIdx].gpslatitude, PROVINCES[provinceIdx].gpslongitude],
                    voivodship: provinceIdx,
                    showMarker: false
                  })
                }}>
                <span>woj. {PROVINCES[provinceIdx].name} ({getPlacesAmountInProvince(provinceIdx, mapLocations)})</span>
                <i className='icon icon--arrow-right' />
              </div>
              {getCitiesWithPlaces(provinceIdx, mapLocations).map((option) => <div
                key={option.city}
                className='select__option group'
                onClick={() => {
                  const name = `${option.city}, ${PROVINCES[provinceIdx].name}`
                  expand(false)
                  setInput(name)
                  setProvinceIdx(-1)
                  setAddress(name, {
                    position: [option.gpslatitude, option.gpslongitude],
                    showMarker: true
                  })
                }}>
                <span>{option.city} pow. {option.district} ({option.amount})</span>
                <i className='icon icon--arrow-right' />
              </div>)}
            </>
            : getProvincesWithPlaces(mapLocations).map((option, idx) => <div
              key={option.search}
              className='select__option group'
              onClick={() => {
                preventClick.current = true
                setProvinceIdx(idx)
              }}>
              <span>woj. {option.name} ({option.amount || 0})</span>
              <i className='icon icon--arrow-right' />
            </div>)}
      </div>
    </div>
  )
}

const getPlacesAmountInProvince = (provinceIdx: number, mapLocations?: Place[]) => {
  if (!mapLocations || mapLocations.length === 0) return 0
  return mapLocations.reduce((r, l) => r += l.province.toLowerCase() === PROVINCES[provinceIdx].search ? 1 : 0, 0)
}

const getProvincesWithPlaces = (mapLocations?: Place[]) => {
  if (!mapLocations || mapLocations.length === 0) return PROVINCES as Province[]
  const provinces = PROVINCES.reduce((r, p) => {
    r[p.search] = { ...p, amount: 0 }
    return r
  }, {} as Record<string, Province>)
  mapLocations.forEach(location => {
    const province = location.province.toLowerCase()
    if (provinces[province]) {
      provinces[province].amount! += 1
    }
  })
  return Object.values(provinces)
}

const getCitiesWithPlaces = (provinceIdx: number, mapLocations?: Place[]) => {
  if (!mapLocations || mapLocations.length === 0) return CITIES[provinceIdx] as City[]
  const cities = CITIES[provinceIdx].reduce((r, p) => {
    r[p.city] = { ...p, amount: 0 }
    return r
  }, {} as Record<string, City>)
  mapLocations.forEach(location => {
    if (cities[location.city]) {
      cities[location.city].amount! += 1
    }
  })
  return Object.values(cities)
}

export default LocationSelect